import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useErrorLog } from '../contexts/ErrorLogContext';

const LEVEL_CONFIG = {
  error: { color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 6%, transparent)', icon: '●', label: 'ERROR', glow: 'color-mix(in srgb, var(--danger) 15%, transparent)' },
  warn:  { color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) 6%, transparent)', icon: '●', label: 'WARN',  glow: 'color-mix(in srgb, var(--warning) 10%, transparent)' },
  info:  { color: '#22d3ee', bg: 'rgba(34, 211, 238, 0.04)',  icon: '●', label: 'INFO',  glow: 'rgba(34, 211, 238, 0.08)' },
};

const SOURCE_COLORS = {
  API:        '#8b5cf6',
  WS:         '#06b6d4',
  CONNECTION: 'var(--warning)',
  FLIGHT:     'var(--success)',
  SYSTEM:     'var(--danger)',
  MISSION:    '#3b82f6',
  DRONE:      '#ec4899',
};

function formatTimestamp(date) {
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}

function formatDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ErrorLogPanel() {
  const { entries, clearLogs } = useErrorLog();
  const [autoScroll, setAutoScroll] = useState(true);
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const logsEndRef = useRef(null);
  const containerRef = useRef(null);

  // Compute available sources from entries
  const availableSources = useMemo(() => {
    const sources = new Set();
    entries.forEach(e => sources.add(e.source));
    return Array.from(sources).sort();
  }, [entries]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      if (filterLevel !== 'all' && entry.level !== filterLevel) return false;
      if (filterSource !== 'all' && entry.source !== filterSource) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          entry.message.toLowerCase().includes(q) ||
          entry.source.toLowerCase().includes(q) ||
          (entry.meta && JSON.stringify(entry.meta).toLowerCase().includes(q))
        );
      }
      return true;
    });
  }, [entries, filterLevel, filterSource, searchQuery]);

  // Count by level
  const counts = useMemo(() => {
    const c = { error: 0, warn: 0, info: 0 };
    entries.forEach(e => { if (c[e.level] !== undefined) c[e.level]++; });
    return c;
  }, [entries]);

  // Auto-scroll
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [filteredEntries, autoScroll]);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      setAutoScroll(scrollHeight - scrollTop - clientHeight < 50);
    }
  };

  const exportLogs = () => {
    const lines = filteredEntries.map(e => {
      const ts = e.timestamp.toISOString();
      const meta = e.meta ? ` | ${JSON.stringify(e.meta)}` : '';
      return `[${ts}] [${e.level.toUpperCase()}] [${e.source}] ${e.message}${meta}`;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `utms_error_log_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalEntries = entries.length;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--bgPrimary)',
      overflow: 'hidden',
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>

      {/* ─── Toolbar ─── */}
      <div style={{
        padding: '12px 16px',
        background: 'var(--bgSecondary)',
        borderBottom: '1px solid var(--borderSecondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        flexShrink: 0,
      }}>
        {/* Row 1: Title + Stats + Actions */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>

          {/* Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" /><path d="M12 17h.01" />
              <path d="M3.262 13.424a1 1 0 0 1 0-.848l7.905-16.632a1 1 0 0 1 1.666 0l7.905 16.632a1 1 0 0 1-.833 1.424H4.095a1 1 0 0 1-.833-1.424z"
                    transform="translate(0, 4) scale(0.85)"
              />
            </svg>
            <span style={{
              color: 'var(--textPrimary)',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}>
              System Logs
            </span>
            <span style={{
              color: 'var(--textMuted)',
              fontSize: '11px',
              fontWeight: 500,
            }}>
              {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {/* Auto-scroll toggle */}
            <button
              onClick={() => setAutoScroll(!autoScroll)}
              title={autoScroll ? 'Auto-scroll ON' : 'Auto-scroll OFF'}
              style={{
                padding: '5px 10px',
                background: autoScroll ? 'rgba(34, 211, 238, 0.12)' : 'var(--bgTertiary)',
                color: autoScroll ? 'var(--accent)' : 'var(--textMuted)',
                border: `1px solid ${autoScroll ? 'rgba(34, 211, 238, 0.3)' : 'var(--borderSecondary)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                transition: 'all 0.2s',
                width: 'auto',
                margin: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="7 13 12 18 17 13" /><line x1="12" y1="6" x2="12" y2="18" />
              </svg>
              Auto
            </button>

            {/* Export */}
            <button
              onClick={exportLogs}
              disabled={filteredEntries.length === 0}
              title="Export visible logs as .txt"
              style={{
                padding: '5px 10px',
                background: 'rgba(59, 130, 246, 0.12)',
                color: '#60a5fa',
                border: '1px solid rgba(59, 130, 246, 0.25)',
                borderRadius: '6px',
                cursor: filteredEntries.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                opacity: filteredEntries.length === 0 ? 0.4 : 1,
                transition: 'all 0.2s',
                width: 'auto',
                margin: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>

            {/* Clear */}
            <button
              onClick={clearLogs}
              disabled={entries.length === 0}
              title="Clear all log entries"
              style={{
                padding: '5px 10px',
                background: entries.length === 0 ? 'var(--bgTertiary)' : 'color-mix(in srgb, var(--danger) 12%, transparent)',
                color: entries.length === 0 ? 'var(--textMuted)' : '#f87171',
                border: `1px solid ${entries.length === 0 ? 'var(--borderSecondary)' : 'color-mix(in srgb, var(--danger) 25%, transparent)'}`,
                borderRadius: '6px',
                cursor: entries.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                opacity: entries.length === 0 ? 0.4 : 1,
                transition: 'all 0.2s',
                width: 'auto',
                margin: 0,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Clear
            </button>
          </div>
        </div>

        {/* Row 2: Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>

          {/* Level filter pills */}
          <div style={{
            display: 'flex',
            gap: '4px',
            background: 'var(--bgPrimary)',
            padding: '3px',
            borderRadius: '8px',
          }}>
            {['error', 'warn', 'info'].map(level => {
              const cfg = LEVEL_CONFIG[level];
              const isActive = filterLevel === level;
              return (
                <button
                  key={level}
                  onClick={() => setFilterLevel(filterLevel === level ? 'all' : level)}
                  title={`${isActive ? 'Show all' : `Filter: ${level}`} (${counts[level]})`}
                  style={{
                    padding: '4px 10px',
                    fontSize: '10px',
                    fontWeight: 700,
                    background: isActive ? cfg.color : 'transparent',
                    color: isActive ? '#fff' : cfg.color,
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    width: 'auto',
                    margin: 0,
                    opacity: isActive ? 1 : 0.8,
                  }}
                >
                  <span style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    background: isActive ? '#fff' : cfg.color,
                    display: 'inline-block',
                    boxShadow: isActive ? 'none' : `0 0 4px ${cfg.color}`,
                  }} />
                  {cfg.label}
                  <span style={{
                    background: isActive ? 'color-mix(in srgb, var(--textPrimary) 25%, transparent)' : `color-mix(in srgb, ${cfg.color} 12.5%, transparent)`,
                    padding: '1px 5px',
                    borderRadius: '4px',
                    fontSize: '9px',
                    fontWeight: 800,
                    lineHeight: '14px',
                  }}>
                    {counts[level]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Source filter */}
          {availableSources.length > 0 && (
            <select
              value={filterSource}
              onChange={e => setFilterSource(e.target.value)}
              style={{
                padding: '5px 10px',
                background: 'var(--bgPrimary)',
                border: '1px solid var(--borderSecondary)',
                color: 'var(--textPrimary)',
                fontSize: '11px',
                fontWeight: 500,
                borderRadius: '6px',
                cursor: 'pointer',
                outline: 'none',
                appearance: 'none',
                WebkitAppearance: 'none',
                paddingRight: '24px',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                margin: 0,
                width: 'auto',
                minWidth: '100px',
              }}
            >
              <option value="all">All Sources</option>
              {availableSources.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

          {/* Search */}
          <div style={{
            position: 'relative',
            flex: '1 1 140px',
            maxWidth: '260px',
            minWidth: '120px',
          }}>
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke={isSearchFocused ? 'var(--accent)' : 'var(--textMuted)'}
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{
                position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                pointerEvents: 'none', transition: 'stroke 0.2s',
              }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              style={{
                width: '100%',
                padding: '5px 10px 5px 30px',
                background: 'var(--bgPrimary)',
                border: `1px solid ${isSearchFocused ? 'var(--accent)' : 'var(--borderSecondary)'}`,
                color: 'var(--textPrimary)',
                fontSize: '11px',
                fontWeight: 500,
                borderRadius: '6px',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                boxShadow: isSearchFocused ? '0 0 0 2px rgba(34, 211, 238, 0.12)' : 'none',
                margin: 0,
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)',
                  background: 'var(--bgTertiary)', border: 'none', borderRadius: '50%',
                  width: '16px', height: '16px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--textMuted)', fontSize: '10px', padding: 0, margin: 0,
                }}
              >
                ×
              </button>
            )}
          </div>

          {/* Active filter indicator */}
          {(filterLevel !== 'all' || filterSource !== 'all' || searchQuery) && (
            <button
              onClick={() => { setFilterLevel('all'); setFilterSource('all'); setSearchQuery(''); }}
              style={{
                padding: '4px 8px',
                background: 'color-mix(in srgb, var(--danger) 10%, transparent)',
                color: '#f87171',
                border: '1px solid color-mix(in srgb, var(--danger) 20%, transparent)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '3px',
                width: 'auto',
                margin: 0,
              }}
            >
              ✕ Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* ─── Log Entries ─── */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Consolas', monospace",
          fontSize: '11.5px',
          minHeight: 0,
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--scrollThumb) transparent',
        }}
      >
        {filteredEntries.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: '300px',
            gap: '16px',
            padding: '40px 20px',
          }}>
            {entries.length === 0 ? (
              <>
                <div style={{
                  width: '64px', height: '64px', borderRadius: '16px',
                  background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--success) 20%, transparent)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: 'var(--textPrimary)', fontSize: '14px', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                    All Systems Operational
                  </div>
                  <div style={{ color: 'var(--textMuted)', fontSize: '12px', fontFamily: "'Inter', sans-serif", marginTop: '4px' }}>
                    No errors logged. System is running clean.
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  width: '64px', height: '64px', borderRadius: '16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: 'var(--textPrimary)', fontSize: '14px', fontWeight: 600, fontFamily: "'Inter', sans-serif" }}>
                    No Matching Entries
                  </div>
                  <div style={{ color: 'var(--textMuted)', fontSize: '12px', fontFamily: "'Inter', sans-serif", marginTop: '4px' }}>
                    {entries.length} total entries hidden by current filters.
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          filteredEntries.map((entry, idx) => {
            const cfg = LEVEL_CONFIG[entry.level] || LEVEL_CONFIG.info;
            const srcColor = SOURCE_COLORS[entry.source] || '#94a3b8';
            const isExpanded = expandedId === entry.id;
            const isFirst = idx === 0;
            const prevEntry = idx > 0 ? filteredEntries[idx - 1] : null;
            const showDateSep = isFirst || (prevEntry && formatDate(prevEntry.timestamp) !== formatDate(entry.timestamp));

            return (
              <React.Fragment key={entry.id}>
                {/* Date separator */}
                {showDateSep && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '8px 16px',
                    background: 'var(--bgSecondary)',
                    borderBottom: '1px solid var(--borderSecondary)',
                  }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--borderSecondary)' }} />
                    <span style={{
                      color: 'var(--textMuted)',
                      fontSize: '10px',
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}>
                      {formatDate(entry.timestamp)}
                    </span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--borderSecondary)' }} />
                  </div>
                )}

                {/* Log entry */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  style={{
                    padding: '8px 16px',
                    borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 3%, transparent)',
                    borderLeft: `3px solid ${cfg.color}`,
                    background: isExpanded ? cfg.glow : 'transparent',
                    cursor: entry.meta ? 'pointer' : 'default',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'color-mix(in srgb, var(--textPrimary) 2%, transparent)'; }}
                  onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                >
                  {/* Main log line — on a narrow screen the message drops below the badges */}
                  <div style={{ display: 'flex', gap: '6px 10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {/* Timestamp */}
                    <span style={{
                      color: 'var(--textMuted)',
                      flexShrink: 0,
                      fontSize: '10.5px',
                      fontWeight: 500,
                      marginTop: '1px',
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-0.2px',
                    }}>
                      {formatTimestamp(entry.timestamp)}
                    </span>

                    {/* Level badge */}
                    <span style={{
                      padding: '1px 7px',
                      fontSize: '9px',
                      fontWeight: 800,
                      color: '#fff',
                      background: cfg.color,
                      borderRadius: '4px',
                      flexShrink: 0,
                      letterSpacing: '0.6px',
                      lineHeight: '16px',
                      fontFamily: "'Inter', sans-serif",
                    }}>
                      {cfg.label}
                    </span>

                    {/* Source badge */}
                    <span style={{
                      padding: '1px 7px',
                      fontSize: '9px',
                      fontWeight: 700,
                      color: srcColor,
                      background: `color-mix(in srgb, ${srcColor} 7%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${srcColor} 14.5%, transparent)`,
                      borderRadius: '4px',
                      flexShrink: 0,
                      lineHeight: '16px',
                      fontFamily: "'Inter', sans-serif",
                      letterSpacing: '0.3px',
                    }}>
                      {entry.source}
                    </span>

                    {/* Message */}
                    <span style={{
                      color: 'var(--textPrimary)',
                      wordBreak: 'break-word',
                      flex: '1 1 220px',
                      lineHeight: '18px',
                    }}>
                      {entry.message}
                    </span>

                    {/* Expand indicator */}
                    {entry.meta && (
                      <span style={{
                        color: 'var(--textMuted)',
                        fontSize: '10px',
                        flexShrink: 0,
                        transition: 'transform 0.2s',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        marginTop: '2px',
                      }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </span>
                    )}
                  </div>

                  {/* Expanded metadata */}
                  {isExpanded && entry.meta && (
                    <div style={{
                      marginTop: '8px',
                      marginLeft: '74px',
                      padding: '10px 14px',
                      background: 'var(--bgPrimary)',
                      border: '1px solid var(--borderSecondary)',
                      borderRadius: '6px',
                      fontSize: '10.5px',
                      color: 'var(--textSecondary)',
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '18px',
                      position: 'relative',
                    }}>
                      {/* Copy button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const text = typeof entry.meta === 'object' ? JSON.stringify(entry.meta, null, 2) : String(entry.meta);
                          navigator.clipboard.writeText(text);
                        }}
                        title="Copy metadata"
                        style={{
                          position: 'absolute', top: '6px', right: '6px',
                          background: 'var(--bgTertiary)', border: '1px solid var(--borderSecondary)',
                          borderRadius: '4px', padding: '3px 6px', cursor: 'pointer',
                          color: 'var(--textMuted)', fontSize: '9px', fontWeight: 600,
                          width: 'auto', margin: 0,
                        }}
                      >
                        Copy
                      </button>
                      <div style={{ color: 'var(--textMuted)', fontSize: '9px', fontWeight: 600, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: "'Inter', sans-serif" }}>
                        Metadata
                      </div>
                      {typeof entry.meta === 'object'
                        ? JSON.stringify(entry.meta, null, 2)
                        : String(entry.meta)}
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={logsEndRef} />
      </div>

      {/* ─── Status Bar ─── */}
      <div style={{
        padding: '6px 16px',
        background: 'var(--bgSecondary)',
        borderTop: '1px solid var(--borderSecondary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        fontSize: '10px',
        fontFamily: "'Inter', sans-serif",
        color: 'var(--textMuted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: counts.error > 0 ? 'var(--danger)' : 'var(--success)',
              display: 'inline-block',
              boxShadow: counts.error > 0 ? '0 0 6px color-mix(in srgb, var(--danger) 50%, transparent)' : '0 0 6px color-mix(in srgb, var(--success) 40%, transparent)',
              animation: counts.error > 0 ? 'none' : undefined,
            }} />
            {counts.error > 0 ? `${counts.error} error${counts.error !== 1 ? 's' : ''}` : 'Healthy'}
          </span>
          <span style={{ color: 'var(--borderSecondary)' }}>│</span>
          <span>
            Showing {filteredEntries.length} of {totalEntries}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {autoScroll && (
            <span style={{ color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px' }}>
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="7 13 12 18 17 13" /><line x1="12" y1="6" x2="12" y2="18" />
              </svg>
              LIVE
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorLogPanel;
